import {Task} from "../Shared/Model/SafetyData";
import {ScoreDataPoints} from "../Shared/Components/Score";
import {Pair} from "../Shared/Model/GameData";

export interface SafetyPairState {
    leftPairClicked?: number | undefined
    rightPairClicked?: number | undefined
    leftWrongPairClicked?: number | undefined
    rightWrongPairClicked?: number | undefined
    pairsCompleted: number[]
}

export interface SafetyMoveState {
    correct: number[]
    wrong: number[]
}

export interface SafetyPlayState {
    task: Task
    isAnswered: boolean
    isAnswerCorrect: boolean
    selectedAnswerIndex: number | undefined
    currentIndex: number
    currentScore: number
    move: SafetyMoveState
    pairs: SafetyPairState | null
}

export default class SafetyManager {

    private readonly tasks: [Task]
    private currentIndex: number = 0
    private currentScore: number = 0

    private answered: boolean = false
    private correct: boolean = false

    private pairs: SafetyPairState = { pairsCompleted: [] }
    private move: SafetyMoveState = { wrong: [], correct: [] }

    private selectedAnswerIndex: number | undefined

    constructor(tasks: [Task]) {
        this.tasks = tasks
    }

    next(): boolean {
        if (this.currentIndex >= this.tasks.length - 1) {
            return false
        }
        this.currentIndex++
        this.answered = false
        this.correct = false
        this.move = { wrong: [], correct: [] }
        this.pairs = { pairsCompleted: [] }
        this.selectedAnswerIndex = undefined
        return true
    }

    answer(correct: boolean, selectedAnswerIndex?: number) {
        this.selectedAnswerIndex = selectedAnswerIndex
        this.correct = correct
        this.answered = true

        if (correct) {
            if (selectedAnswerIndex !== undefined && selectedAnswerIndex >= 0 && selectedAnswerIndex < this.currentTask().answers.length) {
                this.currentScore += this.currentTask().answers[selectedAnswerIndex].points
            } else {
                this.currentScore++;
            }
        }
    }

    skip() {
        this.answer(false)
    }

    shouldDisplaySkip(): boolean {
        return !this.answered && this.currentTask().type === "puzzle"
    }

    shouldDisplayNext(): boolean {
        return this.answered
    }

    canAnswer(): boolean {
        return !this.answered
    }

    score(): ScoreDataPoints {
        return {
            type: "points",
            questionNumber: this.currentIndex + 1,
            questionCount: this.tasks.length,
            score: this.currentScore,
            scoreMax: this.scoreMax(),
        }
    }

    hint(): string | undefined {
        if (this.answered) {
            return this.currentTask().explanation
        }
        return undefined
    }

    currentTask(): Task {
        return this.tasks[this.currentIndex]
    }

    state(): SafetyPlayState {
        return {
            task: this.currentTask(),
            isAnswered: this.answered,
            isAnswerCorrect: this.correct,
            currentScore: this.currentScore,
            selectedAnswerIndex: this.selectedAnswerIndex,
            currentIndex: this.currentIndex,
            pairs: this.pairs,
            move: this.move,
        }
    }

    matchData(): SafetyPairState {
        return this.pairs
    }

    moveData(): SafetyMoveState {
        return this.move
    }

    handleMoveItem(index: number, correct: boolean, toCorrect: boolean): boolean {
        if (this.answered) {
            return false
        } else if (correct === toCorrect) {
            if (this.move.correct.indexOf(index) === -1) {
                this.move.correct.push(index)
                if (this.move.correct.length + this.move.wrong.length >= this.currentTask().answers.length) {
                    this.answered = true
                    this.correct = true
                    this.currentScore++
                }
            }
            return true
        } else if (this.move.wrong.indexOf(index) === -1) {
            this.move.wrong.push(index)
            this.answered = true
            this.correct = false
        }
        return false
    }

    handleLeftPairClicked(pair: Pair, index: number): boolean {
        if (!this.shouldHandlePairAtIndex(index)) {
            return false
        }

        this.pairs.leftPairClicked = index
        if (this.pairs.leftPairClicked !== undefined && this.pairs.rightPairClicked !== undefined) {
            return this.resolvePairs()
        }
        return false
    }

    handleRightPairClicked(pair: Pair, index: number): boolean {
        if (!this.shouldHandlePairAtIndex(index)) {
            return false
        }

        this.pairs.rightPairClicked = index
        if (this.pairs.leftPairClicked !== undefined && this.pairs.rightPairClicked !== undefined)  {
            return this.resolvePairs()
        }
        return false
    }

    filteredPairs(): Pair[] | undefined {
        if (this.currentTask().pairs !== undefined) {
            return this.currentTask().pairs.filter((pair) => pair.rightPart.length > 0)
        }
    }

    private shouldHandlePairAtIndex(index: number): boolean {
        return !this.answered && this.pairs.pairsCompleted.indexOf(index) === -1
    }

    private resolvePairs(): boolean {
        let result = false
        if (this.pairs.rightPairClicked === this.pairs.leftPairClicked) {
            this.pairs.leftWrongPairClicked = undefined
            this.pairs.rightWrongPairClicked = undefined
            this.pairs.pairsCompleted.push(this.pairs.leftPairClicked)
            result = true
            if (this.pairs.pairsCompleted.length >= this.filteredPairs().length) {
                this.answered = true
                this.correct = true
                this.currentScore ++
            }
        } else {
            this.answered = true
            this.correct = false
            this.pairs.leftWrongPairClicked = this.pairs.leftPairClicked
            this.pairs.rightWrongPairClicked = this.pairs.rightPairClicked
        }
        this.pairs.leftPairClicked = undefined
        this.pairs.rightPairClicked = undefined
        return result
    }


    private scoreMax(): number {
        return this.tasks.length
    }
}